/**
 * Infinite scroll for the "Product grid" block on collection pages.
 * Loads the next page of products via fetch and appends them to the
 * existing grid when the sentinel element scrolls into view, instead of
 * showing numbered pagination links.
 */
(function () {
  'use strict';

  var REVEAL_CLASS = 'infinite-scroll-item';
  var STYLE_ID = 'infinite-scroll-reveal-style';

  // Impulse's scroll-reveal animation only scans the DOM once, at page
  // load, so products appended later by infinite scroll never get
  // "revealed" and stay in whatever pre-animation (often invisible) state
  // the theme's CSS/JS puts them in by default — regardless of whether
  // that's driven by an attribute, a class, or the <image-element> custom
  // element's own internal state. Rather than guess the exact mechanism,
  // inject one small stylesheet that forcibly overrides visibility for
  // anything marked with our own class, with maximum specificity, so it
  // wins regardless of how the theme is hiding it.
  function ensureRevealStylesheet() {
    if (document.getElementById(STYLE_ID)) return;
    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.' + REVEAL_CLASS + ', .' + REVEAL_CLASS + ' * {' +
      'opacity: 1 !important;' +
      'visibility: visible !important;' +
      'transform: none !important;' +
      '-webkit-transform: none !important;' +
      'transition: none !important;' +
      'animation: none !important;' +
      '}';
    document.head.appendChild(style);
  }

  function revealAppendedContent(item) {
    // Belt-and-braces: strip any scroll-reveal attributes so attribute
    // based CSS selectors can no longer match either.
    var aosNodes = item.querySelectorAll('[data-aos]');
    var allAosNodes = item.hasAttribute && item.hasAttribute('data-aos')
      ? [item].concat(Array.prototype.slice.call(aosNodes))
      : Array.prototype.slice.call(aosNodes);

    allAosNodes.forEach(function (node) {
      node.removeAttribute('data-aos');
      node.removeAttribute('data-aos-offset');
      node.removeAttribute('data-aos-delay');
    });

    // Primary fix: mark the item so our injected stylesheet forces it
    // (and everything inside it, including <image-element>/<img>) fully
    // visible, no matter what mechanism the theme itself uses to hide it.
    item.classList.add(REVEAL_CLASS);
  }

  function initInfiniteScroll(wrapper) {
    var sentinel = wrapper.querySelector('[data-infinite-scroll-sentinel]');
    var grid = wrapper.querySelector('.grid--uniform');

    if (!sentinel || !grid) return;

    var loading = false;
    var nextUrl = sentinel.getAttribute('data-next-url');

    var loadingIndicator = sentinel.querySelector('[data-infinite-scroll-loading]');

    function setLoading(state) {
      loading = state;
      if (loadingIndicator) {
        loadingIndicator.style.display = state ? '' : 'none';
      }
    }

    function loadNextPage() {
      if (loading || !nextUrl) return;
      setLoading(true);

      // ZDIFY: cache: 'no-store' forces a fresh server round-trip for every
      // pagination request instead of letting the browser silently reuse a
      // cached copy of this exact URL from earlier in the session. Theme
      // edits (like product-card CSS/markup fixes) are invisible until the
      // page is actually re-fetched, and without this a shopper - or a
      // reviewer testing a fix - can keep seeing stale, already-cached
      // pages 2+ even after the live theme has changed underneath them.
      fetch(nextUrl, { credentials: 'same-origin', cache: 'no-store' })
        .then(function (response) {
          if (!response.ok) throw new Error('Request failed: ' + response.status);
          return response.text();
        })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var newGrid = doc.querySelector('.grid--uniform');
          var newSentinel = doc.querySelector('[data-infinite-scroll-sentinel]');

          if (newGrid) {
            ensureRevealStylesheet();

            var newItems = Array.prototype.slice.call(newGrid.children);

            newItems.forEach(function (item) {
              revealAppendedContent(item);
            });

            var fragment = document.createDocumentFragment();
            newItems.forEach(function (child) {
              fragment.appendChild(child);
            });
            grid.appendChild(fragment);

            // Run again on the next two animation frames: some image
            // loading/animation libraries add their own inline styles
            // asynchronously (e.g. after the image decodes) which could
            // otherwise re-hide the item a moment after insertion.
            requestAnimationFrame(function () {
              newItems.forEach(revealAppendedContent);
              requestAnimationFrame(function () {
                newItems.forEach(revealAppendedContent);
              });
            });

            if (window.theme && typeof window.theme.initQuickShop === 'function') {
              window.theme.initQuickShop(grid);
            }
            document.dispatchEvent(
              new CustomEvent('infinite-scroll:loaded', { detail: { grid: grid, items: newItems } })
            );
          }

          if (newSentinel && newSentinel.getAttribute('data-next-url')) {
            nextUrl = newSentinel.getAttribute('data-next-url');
          } else {
            nextUrl = null;
            observer.unobserve(sentinel);
            sentinel.style.display = 'none';
          }
        })
        .catch(function (error) {
          console.error('Infinite scroll failed to load next page:', error);
          nextUrl = null;
          observer.unobserve(sentinel);
        })
        .finally(function () {
          setLoading(false);
        });
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) loadNextPage();
        });
      },
      { rootMargin: '400px 0px 400px 0px' }
    );

    observer.observe(sentinel);
  }

  function init() {
    var wrappers = document.querySelectorAll('[data-infinite-scroll-wrapper]');
    wrappers.forEach(initInfiniteScroll);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init after Impulse's AJAX filter/sort content swap, if it fires an event.
  document.addEventListener('collection:reloaded', init);
})();
